import { invalidateValidatedOrganizations } from './accessValidation';
import { clearSessionEtags } from './axiosInterceptors';
import { clearUserScopedCaches } from './cacheKeys';
import { invalidateOrganizationList } from './organizationList';
import { getSessionKeycloak, getSessionSubject, setSessionKeycloak, stopProactiveTokenRefresh } from './token';

let sessionLogoutLatched = false;
const sessionLogoutListeners = new Set<() => void>();

export const isSessionLogoutLatched = (): boolean => sessionLogoutLatched;
export const subscribeSessionLogout = (listener: () => void): (() => void) => {
  sessionLogoutListeners.add(listener);
  return () => sessionLogoutListeners.delete(listener);
};

const setSessionLogoutLatched = (latched: boolean): void => {
  if (sessionLogoutLatched === latched) return;
  sessionLogoutLatched = latched;
  for (const listener of sessionLogoutListeners) listener();
};

export const clearSessionForLogout = (subject = getSessionSubject()): void => {
  if (subject) clearUserScopedCaches(subject);
  clearSessionEtags();
  invalidateValidatedOrganizations();
  invalidateOrganizationList();
  stopProactiveTokenRefresh();
};

export const runSessionLogout = async <T>(logout: () => T | Promise<T>): Promise<Awaited<T>> => {
  const capturedClient = getSessionKeycloak();
  const capturedSubject = getSessionSubject();
  setSessionLogoutLatched(true);

  try {
    setSessionKeycloak(null);
    clearSessionForLogout(capturedSubject);
    return await logout();
  } catch (error) {
    setSessionLogoutLatched(false);
    if (capturedClient?.authenticated && capturedClient.token) setSessionKeycloak(capturedClient);
    throw error;
  }
};

export const __resetSessionLogoutForTests = (): void => {
  sessionLogoutLatched = false;
  sessionLogoutListeners.clear();
};
